import * as React from 'react';
import { ReactNode, useCallback, useId, useRef, useState } from 'react';
import { PickListPanel } from './PickListPanel';
import { PickListControls } from './PickListControls';
import { useItemSelection } from './useItemSelection';
import './PickList.css';

export interface PickListItem {
  id: string;
}

export interface PickListProps<T extends PickListItem> {
  source: T[];
  target: T[];
  onChange: (event: { source: T[]; target: T[] }) => void;
  itemTemplate: (item: T, isTarget: boolean) => ReactNode;
  sourceHeader?: ReactNode;
  targetHeader?: ReactNode;
  sourceLabel?: string;
  targetLabel?: string;
}

export function PickList<T extends PickListItem>({
  source,
  target,
  onChange,
  itemTemplate,
  sourceHeader,
  targetHeader,
  sourceLabel = 'Available',
  targetLabel = 'Selected',
}: PickListProps<T>) {
  const [selectedSourceItems, setSelectedSourceItems] = useState<Set<string>>(
    new Set(),
  );
  const [selectedTargetItems, setSelectedTargetItems] = useState<Set<string>>(
    new Set(),
  );

  const baseId = useId();
  const sourceListId = `${baseId}-source-list`;
  const targetListId = `${baseId}-target-list`;

  const sourceListRef = useRef<HTMLDivElement>(null);
  const targetListRef = useRef<HTMLDivElement>(null);

  const { handleItemClick: handleSourceItemClickSelection } = useItemSelection(
    source,
    selectedSourceItems,
  );

  const { handleItemClick: handleTargetItemClickSelection } = useItemSelection(
    target,
    selectedTargetItems,
  );

  const handleSourceItemClick = useCallback(
    (itemId: string, event: React.MouseEvent) => {
      const newSelection = handleSourceItemClickSelection(itemId, event);
      setSelectedSourceItems(newSelection);
    },
    [handleSourceItemClickSelection],
  );

  const handleTargetItemClick = useCallback(
    (itemId: string, event: React.MouseEvent) => {
      const newSelection = handleTargetItemClickSelection(itemId, event);
      setSelectedTargetItems(newSelection);
    },
    [handleTargetItemClickSelection],
  );

  const moveToTarget = useCallback(() => {
    const itemsToMove = source.filter((item) =>
      selectedSourceItems.has(item.id),
    );
    const remainingSource = source.filter(
      (item) => !selectedSourceItems.has(item.id),
    );

    onChange({
      source: remainingSource,
      target: [...target, ...itemsToMove],
    });

    setSelectedSourceItems(new Set());
  }, [source, target, selectedSourceItems, onChange]);

  const moveAllToTarget = useCallback(() => {
    onChange({
      source: [],
      target: [...target, ...source],
    });

    setSelectedSourceItems(new Set());
  }, [source, target, onChange]);

  const moveToSource = useCallback(() => {
    const itemsToMove = target.filter((item) =>
      selectedTargetItems.has(item.id),
    );
    const remainingTarget = target.filter(
      (item) => !selectedTargetItems.has(item.id),
    );

    onChange({
      source: [...source, ...itemsToMove],
      target: remainingTarget,
    });

    setSelectedTargetItems(new Set());
  }, [source, target, selectedTargetItems, onChange]);

  const moveAllToSource = useCallback(() => {
    onChange({
      source: [...source, ...target],
      target: [],
    });

    setSelectedTargetItems(new Set());
  }, [source, target, onChange]);

  return (
    <div className="bmwe-picklist" role="group">
      <PickListPanel
        items={source}
        selectedItems={selectedSourceItems}
        listId={sourceListId}
        listRef={sourceListRef}
        header={sourceHeader}
        label={sourceLabel}
        isTarget={false}
        itemTemplate={itemTemplate}
        onItemClick={handleSourceItemClick}
      />

      <PickListControls
        canMoveToTarget={selectedSourceItems.size > 0}
        canMoveAllToTarget={source.length > 0}
        canMoveToSource={selectedTargetItems.size > 0}
        canMoveAllToSource={target.length > 0}
        onMoveToTarget={moveToTarget}
        onMoveAllToTarget={moveAllToTarget}
        onMoveToSource={moveToSource}
        onMoveAllToSource={moveAllToSource}
      />

      <PickListPanel
        items={target}
        selectedItems={selectedTargetItems}
        listId={targetListId}
        listRef={targetListRef}
        header={targetHeader}
        label={targetLabel}
        isTarget={true}
        itemTemplate={itemTemplate}
        onItemClick={handleTargetItemClick}
      />
    </div>
  );
}
